<template>
  <div class="mobile-page">
    <div class="mobile-header me-header">
      <h2><el-icon><UserFilled /></el-icon> 我的</h2>
      <p>平台说明、数据源状态与全部功能入口</p>
    </div>

    <div class="mobile-body">
      <!-- 用户卡 -->
      <div class="user-card">
        <div class="avatar"><el-icon :size="28"><User /></el-icon></div>
        <div class="user-info">
          <div class="user-name">移动端安全巡检平台</div>
          <div class="user-role">安全巡检管理后台 · 移动版（免登录）</div>
        </div>
      </div>

      <!-- 数据源状态 -->
      <div class="white-card">
        <div class="wc-title">数据源状态</div>
        <div class="src-row">
          <span class="src-name">巡检管理后台</span>
          <span class="pill" :class="inspectOk ? 'pill-green' : 'pill-red'">{{ inspectOk ? '在线' : '异常' }}</span>
          <span class="src-url">{{ inspectHost }}</span>
        </div>
        <div class="src-row">
          <span class="src-name">信息上报后台</span>
          <template v-if="gatherEnabled">
            <span class="pill" :class="gatherPillClass">{{ gatherText }}</span>
            <span class="src-url">{{ gatherHost }}</span>
          </template>
          <template v-else>
            <span class="pill pill-gray">未配置</span>
            <span class="src-url">VITE_API_BASE</span>
          </template>
        </div>
        <el-button size="small" plain style="width:100%;margin-top:10px" @click="check">重新检测连接</el-button>
      </div>

      <!-- 全部功能 -->
      <div class="section-title"><span>全部功能</span></div>
      <div class="white-card no-pad">
        <div class="menu-item" v-for="m in menus" :key="m.to" @click="$router.push(m.to)">
          <span class="mi-circle" :style="{ background: m.bg, color: m.color }"><el-icon :size="16"><component :is="m.icon" /></el-icon></span>
          <span class="mi-text">{{ m.name }}</span>
          <el-icon class="mi-arrow"><ArrowRight /></el-icon>
        </div>
      </div>

      <!-- 说明 -->
      <div class="about-box">
        <p><b>移动端安全巡检平台</b> · 电脑端「安全巡检管理」后台的移动版</p>
        <p>本平台免登录，打开即可查看：总览 / 巡检计划 / 巡检安排 / 值班领导 / 异常报告 / 巡检记录 / 隐患闭环，数据与电脑端管理后台同源、实时同步。</p>
        <p class="about-sub">巡检后台：{{ inspectHost }}<template v-if="gatherEnabled">；上报后台：{{ gatherHost }}</template></p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  gatherEnabled, gatherBase, inspectBase, fetchOverview, fetchGatherRecords, isAuthError
} from '../api'

const inspectOk = ref(false)
const gatherOk = ref(false)
const gatherAuth = ref(false)
const inspectHost = hostShort(inspectBase)
const gatherHost = gatherEnabled() ? hostShort(gatherBase) : ''

function hostShort(base) {
  try { return new URL(base).host } catch { return base }
}

const allMenus = [
  { name: '总览', to: '/overview', icon: 'DataBoard', bg: '#e8f0fe', color: '#1a73e8' },
  { name: '巡检计划', to: '/plans', icon: 'Calendar', bg: '#e8f0fe', color: '#1a73e8' },
  { name: '巡检安排', to: '/duty', icon: 'Timer', bg: '#fef7e0', color: '#b06000' },
  { name: '值班领导', to: '/duty-leaders', icon: 'UserFilled', bg: '#f3e8fd', color: '#8430ce' },
  { name: '异常报告', to: '/hazards?mode=reports', icon: 'Warning', bg: '#fce8e6', color: '#c5221f' },
  { name: '巡检记录', to: '/records-inspect', icon: 'Checked', bg: '#e6f4ea', color: '#188038' },
  { name: '隐患闭环', to: '/hazards?mode=closure', icon: 'Finished', bg: '#e0f2f1', color: '#00695c' },
  { name: '上报信息', to: '/gather', icon: 'Upload', bg: '#e8f0fe', color: '#0b57d0', gather: true }
]
const menus = computed(() => allMenus.filter(m => !m.gather || gatherEnabled()))

const gatherText = computed(() => {
  if (gatherOk.value) return '在线'
  if (gatherAuth.value) return '需登录'
  return '异常'
})
const gatherPillClass = computed(() => {
  if (gatherOk.value) return 'pill-green'
  if (gatherAuth.value) return 'pill-orange'
  return 'pill-red'
})

async function check() {
  try {
    await fetchOverview()
    inspectOk.value = true
  } catch {
    inspectOk.value = false
  }
  if (gatherEnabled()) {
    try {
      await fetchGatherRecords({ page: 1, pageSize: 1 })
      gatherOk.value = true
      gatherAuth.value = false
    } catch (e) {
      gatherOk.value = false
      gatherAuth.value = isAuthError(e)
    }
  }
}

onMounted(check)
</script>

<style scoped>
.user-card {
  display: flex; align-items: center; gap: 14px; background: #fff; border-radius: 16px;
  padding: 16px; margin-bottom: 10px; box-shadow: 0 3px 12px rgba(31,45,61,.06);
}
.avatar {
  width: 54px; height: 54px; border-radius: 50%;
  background: linear-gradient(135deg, #1a73e8, #4285f4); color: #fff;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.user-name { font-size: 16px; font-weight: 700; color: #202124; }
.user-role { font-size: 12px; color: #9aa0a6; margin-top: 3px; }

.wc-title { font-size: 13.5px; font-weight: 700; color: #303133; margin-bottom: 8px; }
.src-row { display: flex; align-items: center; gap: 8px; padding: 6px 0; font-size: 13px; color: #3c4043; }
.src-name { width: 92px; flex-shrink: 0; color: #5f6368; }
.src-url { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #9aa0a6; font-size: 12px; }

.no-pad { padding: 4px 0; }
.menu-item {
  display: flex; align-items: center; gap: 10px; padding: 12px 14px;
  border-bottom: 1px solid #f6f7f9; cursor: pointer;
}
.menu-item:last-child { border-bottom: none; }
.menu-item:active { background: #f8f9fb; }
.mi-circle {
  width: 30px; height: 30px; border-radius: 9px; display: flex;
  align-items: center; justify-content: center; flex-shrink: 0;
}
.mi-text { font-size: 14px; color: #303133; flex: 1; }
.mi-arrow { color: #c0c4cc; }

.about-box {
  background: #e8f0fe; border-radius: 12px; padding: 12px 14px; margin: 12px 0;
  font-size: 12.5px; color: #4d669e; line-height: 1.7;
}
.about-sub { opacity: .85; margin-top: 4px; word-break: break-all; }
</style>
